/**
 * Authentication Service for Majalisna Application
 * 
 * This service handles all authentication-related API calls.
 */

import API from '../../config/api';
import { publicApiClient, privateApiClient, handleApiError } from '../apiClient';

/**
 * Login with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<Object>} User data with token
 */
export const login = async (email, password) => {
  try {
    // Create form data for login (backend expects form data)
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);
    
    const response = await publicApiClient.post(API.auth.login, formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Logout the current user
 * @returns {Promise<Object>} Logout response
 */
export const logout = async () => {
  try {
    const response = await privateApiClient.post(API.auth.logout);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Register a new user
 * @param {Object} userData - User registration data
 * @returns {Promise<Object>} New user data
 */
export const register = async (userData) => {
  try {
    const response = await publicApiClient.post(API.auth.register, userData);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Reset user password with token
 * @param {Object} data - Password reset data with token
 * @returns {Promise<Object>} Reset response
 */
export const resetPassword = async (data) => {
  try {
    const response = await publicApiClient.post(API.auth.resetPassword, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Request password reset email
 * @param {string} email - User email
 * @returns {Promise<Object>} Response with message
 */
export const forgotPassword = async (email) => {
  try {
    const response = await publicApiClient.post(API.auth.forgotPassword, { email });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get current user profile
 * @returns {Promise<Object>} User profile data
 */
export const getCurrentUser = async () => {
  try {
    const response = await privateApiClient.get(API.auth.me);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Refresh the authentication token
 * @returns {Promise<Object>} New token data
 */
export const refreshToken = async () => {
  try {
    const response = await privateApiClient.post(API.auth.refreshToken);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// Export all functions as a service object
const authService = {
  login,
  logout,
  register,
  resetPassword,
  forgotPassword,
  getCurrentUser,
  refreshToken
};

export default authService;
